import PropTypes from 'prop-types';
import React from 'react';

import { ContentZonesService, LocaleService } from '@sm360/widget-commons-core-services';
import { AnalyticsService, VIRTUAL_PAGEVIEWS as PAGES } from '@sm360/widget-commons-general';
import { PurchaseMethods, Quote } from '@sm360/widget-commons-vehicle';

import ProtectionPackage from 'protections/components/ProtectionCategory/ProtectionPackage/ProtectionPackage.jsx';
import { PROTECTIONS } from 'protections/constants/protections.js';

import 'protections/components/ProtectionCategory/ProtectionCategory.scss';

const ProtectionCategory = ({
    isLease,
    nextPreviousProtectionCategory,
    openProtectionCategory,
    protectionCategory,
    selectedFinanceTerm,
    selectProtectionPackage,
    taxCodes,
    taxesFactor,
}) => {
    const protectionPackages =
        protectionCategory.selected &&
        protectionCategory.itemsPackages.map((protectionPackage) => {
            if (
                protectionPackage.selectionCode === PROTECTIONS.INSURANCE ||
                protectionPackage.selectionCode === PROTECTIONS.PROTECTION
            ) {
                AnalyticsService.virtualPageView(PAGES.CHECKOUT_PROTECTIONS_INSURANCE());
            }
            return (
                <ProtectionPackage
                    key={protectionPackage.selectionCode}
                    isLease={isLease}
                    selectedFinanceTerm={selectedFinanceTerm}
                    taxesFactor={taxesFactor}
                    taxCodes={taxCodes}
                    protectionCategory={protectionCategory}
                    protectionPackage={protectionPackage}
                    protectionPackageItems={protectionCategory.getItemsPackageContent(protectionPackage.selectionCode)}
                    webUrl={protectionCategory.webUrl}
                    selectProtectionPackage={selectProtectionPackage}
                />
            );
        });

    let selectedProtectionsCountLabel = '';
    const selectedProtectionsPackagesCount = protectionCategory.getSelectedItemsPackagesCount();
    const selectedProtectionsCount = protectionCategory.getSelectedItemsCount();
    if (protectionCategory.declined) {
        selectedProtectionsCountLabel = LocaleService.t('PROTECTIONS.DECLINED_OPTION', {
            option: protectionCategory.description,
        });
    } else if (selectedProtectionsPackagesCount === 1) {
        selectedProtectionsCountLabel = LocaleService.t('PROTECTIONS.SELECTED_COUNT_1');
    } else if (selectedProtectionsPackagesCount > 1) {
        selectedProtectionsCountLabel = LocaleService.t('PROTECTIONS.SELECTED_COUNT');
    }

    if (selectedProtectionsCount > selectedProtectionsPackagesCount) {
        selectedProtectionsCountLabel += ` ${LocaleService.t('PROTECTIONS.INCLUDED_COUNT', {
            count: selectedProtectionsCount,
        })}`;
    }

    const selectedProtectionsCountValue =
        selectedProtectionsCountLabel && selectedProtectionsPackagesCount > 0 ? selectedProtectionsPackagesCount : '';

    const brochure = protectionCategory.webUrl && (
        <a href={protectionCategory.webUrl} className="brochure is-util" rel="noopener noreferrer" target="_blank">
            <icon className="icon-brochure" />
            {LocaleService.t('PROTECTIONS.VIEW_BROCHURE')}
        </a>
    );

    const enabledClosedBar = !protectionCategory.selected &&
        protectionCategory.enabled && (
            <div className="protection-category--modify">
                <span className="is-util amount">{selectedProtectionsCountValue}</span> {selectedProtectionsCountLabel}
                <button className="sr-link" onClick={() => openProtectionCategory(protectionCategory)} type="button">
                    {LocaleService.t('PROTECTIONS.MODIFY')}
                </button>
            </div>
        );

    const enabledOpenedBar = protectionCategory.selected &&
        protectionCategory.enabled && (
            <div className="protection-category--buttonsWrapper">
                <button
                    className="sr-button-outline-1"
                    onClick={() => nextPreviousProtectionCategory(protectionCategory, false, true)}
                    type="button"
                >
                    {LocaleService.t('PROTECTIONS.DECLINE_OPTION', { option: protectionCategory.description })}
                </button>
                <button
                    className="sr-button-1 is-small"
                    onClick={() => nextPreviousProtectionCategory(protectionCategory, false, false)}
                    type="button"
                >
                    {LocaleService.t('PROTECTIONS.DONE')}
                </button>
            </div>
        );

    let wrapperProtectionClass;
    if (protectionCategory.selected) {
        wrapperProtectionClass = 'protection-category--wrapper is-open';
    } else if (!protectionCategory.selected && protectionCategory.enabled) {
        wrapperProtectionClass = 'protection-category--wrapper is-validated';
    } else {
        wrapperProtectionClass = 'protection-category--wrapper';
    }

    let czGroupName;
    let protectionSlug;
    const widgetname = 'widget.protections.';
    const czPackage = [];

    switch (protectionCategory.id) {
        case PROTECTIONS.EXTENDED_WARRANTY:
            protectionSlug = 'extended.warranty';
            czGroupName = `${widgetname}${protectionSlug}`;
            break;

        case PROTECTIONS.INSURANCE:
            protectionSlug = 'insurance';
            czGroupName = `${widgetname}${protectionSlug}`;
            break;

        case PROTECTIONS.COSMETIC_PROTECTION:
            protectionSlug = 'cosmetic.protection';
            czGroupName = `${widgetname}${protectionSlug}`;
            break;

        case PROTECTIONS.PROTECTION:
            protectionSlug = 'protection';
            czGroupName = `${widgetname}${protectionSlug}`;
            break;

        default:
            break;
    }

    czPackage.BG = ContentZonesService.getContentZone(czGroupName, `${protectionSlug}.headline.img`);
    czPackage.BG = czPackage.BG ? { backgroundImage: `url(' ${czPackage.BG.getValue()}` } : {};
    czPackage.text = ContentZonesService.getContentZone(czGroupName, `${protectionSlug}.headline.text`);
    czPackage.text = czPackage.text ? czPackage.text.getValue() : '';
    czPackage.picto = ContentZonesService.getContentZone(czGroupName, `${protectionSlug}.picto`);
    czPackage.picto = czPackage.picto ? czPackage.picto.getValue() : '';

    return (
        <div className={wrapperProtectionClass}>
            <div className="protection-category--intro" style={czPackage.BG}>
                <img
                    src={czPackage.picto}
                    className="protection-category--picto"
                    alt={protectionCategory.description}
                />
                <span className="sr-protection-category-title">{protectionCategory.description}</span>
                {brochure}
                <div className="sr-protection-category-descrition">{czPackage.text}</div>
            </div>
            <div className="protection-category--content">{protectionPackages}</div>

            {enabledClosedBar}
            {enabledOpenedBar}
        </div>
    );
};

ProtectionCategory.propTypes = {
    isLease: PropTypes.bool.isRequired,
    nextPreviousProtectionCategory: PropTypes.func.isRequired,
    openProtectionCategory: PropTypes.func.isRequired,
    protectionCategory: PropTypes.instanceOf(Quote.QuoteItemCategoryModel).isRequired,
    selectedFinanceTerm: PropTypes.instanceOf(PurchaseMethods.TermModel),
    selectProtectionPackage: PropTypes.func.isRequired,
    taxCodes: PropTypes.string.isRequired,
    taxesFactor: PropTypes.number.isRequired,
};

ProtectionCategory.displayName = 'ProtectionCategory';

export default ProtectionCategory;



// WEBPACK FOOTER //
// ./src/components/ProtectionCategory/ProtectionCategory.jsx